import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries, AreaSeries } from 'lightweight-charts';
import { getEquityCurve } from '../../api/api';

/**
 * Build equity curve from trades array (fallback when API has no data)
 * @param {Array} trades - Array of trade objects with actual_profit_loss and created_at/closed_at
 * @param {number} initialBalance - Starting balance (default 0 for cumulative P/L)
 * @returns {Array} Array of { date, equity, drawdown, runup } points
 */
const buildEquityCurveFromTrades = (trades, initialBalance = 0) => {
  if (!trades || trades.length === 0) return [];

  // Sort trades by close date (or created_at as fallback)
  const sortedTrades = [...trades]
    .filter(t => t.actual_profit_loss != null)
    .sort((a, b) => {
      const dateA = new Date(a.closed_at || a.created_at || 0).getTime();
      const dateB = new Date(b.closed_at || b.created_at || 0).getTime();
      return dateA - dateB;
    });

  if (sortedTrades.length === 0) return [];

  let cumulative = initialBalance;
  let peak = cumulative;
  let trough = cumulative;
  const points = [];

  sortedTrades.forEach(trade => {
    const pnl = parseFloat(trade.actual_profit_loss) || 0;
    const dateStr = trade.closed_at || trade.created_at;
    if (!dateStr) return;

    cumulative += pnl;
    peak = Math.max(peak, cumulative);
    trough = Math.min(trough, cumulative);

    const drawdown = Math.max(0, peak - cumulative);
    const runup = Math.max(0, cumulative - trough);

    points.push({
      date: dateStr,
      equity: cumulative,
      drawdown,
      runup,
    });
  });

  return points;
};

/**
 * EquityCurveWidget - Displays equity curve chart with optional runup/drawdown bands
 * 
 * @param {Object} props
 * @param {string} props.accountId - Account UUID to fetch equity data for
 * @param {string} props.startDate - Start date filter (YYYY-MM-DD)
 * @param {string} props.endDate - End date filter (YYYY-MM-DD)
 * @param {Array} props.trades - Fallback trades array for client-side calculation
 * @param {Array} props.equityCurveData - Pre-calculated equity curve from backend stats
 * @param {boolean} props.hideTitle - Whether to hide the widget title (used when embedded in grid)
 */
const EquityCurveWidget = ({ accountId, startDate, endDate, trades, equityCurveData, hideTitle = false }) => {
    const chartContainerRef = useRef();
    const chartRef = useRef(null);
    const equitySeriesRef = useRef(null);
    const upperSeriesRef = useRef(null); // equity + runup
    const lowerSeriesRef = useRef(null); // equity - drawdown
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!chartContainerRef.current) return;
        if (!accountId && (!trades || trades.length === 0)) {
            setError('No account or trades data provided.');
            return;
        }

        // Track whether this effect has been cleaned up (component unmounted or deps changed)
        let disposed = false;

        // Cleanup previous chart
        if (chartRef.current) {
            chartRef.current.remove();
            chartRef.current = null;
        }

        const chart = createChart(chartContainerRef.current, {
            autoSize: true,
            layout: {
                background: { type: 'solid', color: 'transparent' },
                textColor: getComputedStyle(document.documentElement).getPropertyValue('--color-text-primary')?.trim() || '#f3f4f6',
                attributionLogo: false,
            },
            grid: {
                vertLines: { color: 'rgba(100, 116, 139, 0.25)' },
                horzLines: { color: 'rgba(100, 116, 139, 0.25)' },
            },
            timeScale: {
                borderColor: 'rgba(100, 116, 139, 0.35)',
                timeVisible: true,
            },
            rightPriceScale: {
                borderColor: 'rgba(100, 116, 139, 0.35)',
            },
            crosshair: {
                mode: 0, // CrosshairMode.Normal
            },
        });

        // Main equity line
        const equitySeries = chart.addSeries(LineSeries, {
            color: '#0ea5e9',
            lineWidth: 2,
            priceLineVisible: false,
        });

        // Upper shadow (equity + runup) - using area for visual effect
        const upperSeries = chart.addSeries(LineSeries, {
            color: 'rgba(16, 185, 129, 0.4)',
            lineWidth: 1,
            lineStyle: 2, // Dashed
            priceLineVisible: false,
        });

        // Lower shadow (equity - drawdown)
        const lowerSeries = chart.addSeries(LineSeries, {
            color: 'rgba(239, 68, 68, 0.4)',
            lineWidth: 1,
            lineStyle: 2, // Dashed
            priceLineVisible: false,
        });

        chartRef.current = chart;
        equitySeriesRef.current = equitySeries;
        upperSeriesRef.current = upperSeries;
        lowerSeriesRef.current = lowerSeries;

        const processAndSetData = (rawData) => {
            if (disposed) return false;
            if (!rawData || rawData.length === 0) {
                setError('No equity data available for the selected period.');
                equitySeries.setData([]);
                upperSeries.setData([]);
                lowerSeries.setData([]);
                return false;
            }

            const mapped = rawData
                .map((item) => {
                    const dateValue = item.date || item.timestamp;
                    if (!dateValue) return null;
                    
                    const time = Math.floor(new Date(dateValue).getTime() / 1000);
                    const equity = parseFloat(item.equity);
                    
                    if (isNaN(time) || !isFinite(time) || isNaN(equity) || !isFinite(equity)) {
                        return null;
                    }
                    
                    const rawDrawdown = item.drawdown != null ? parseFloat(item.drawdown) : 0;
                    const rawRunup = item.runup != null ? parseFloat(item.runup) : 0;

                    const drawdown = isNaN(rawDrawdown) ? 0 : Math.abs(rawDrawdown);
                    const runup = isNaN(rawRunup) ? 0 : Math.abs(rawRunup);

                    return {
                        time,
                        equity,
                        lower: equity - drawdown,
                        upper: equity + runup,
                    };
                })
                .filter(item => item !== null)
                .sort((a, b) => a.time - b.time);

            // Remove duplicates (same timestamp)
            const deduped = [];
            const seenTimes = new Set();
            for (const item of mapped) {
                if (!seenTimes.has(item.time)) {
                    seenTimes.add(item.time);
                    deduped.push(item);
                }
            }

            if (deduped.length === 0) {
                setError('No valid equity data points found.');
                equitySeries.setData([]);
                upperSeries.setData([]);
                lowerSeries.setData([]);
                return false;
            }

            const equityData = deduped.map(({ time, equity }) => ({ time, value: equity }));
            const upperData = deduped.map(({ time, upper }) => ({ time, value: upper }));
            const lowerData = deduped.map(({ time, lower }) => ({ time, value: lower }));

            equitySeries.setData(equityData);
            upperSeries.setData(upperData);
            lowerSeries.setData(lowerData);

            chart.timeScale().fitContent();
            setError(null);
            return true;
        };

        const fetchChartData = async () => {
            if (disposed) return;
            setLoading(true);
            setError(null);
            
            try {
                // Priority 1: Use pre-calculated equity curve from backend stats (most accurate)
                if (equityCurveData && equityCurveData.length > 0) {
                    const success = processAndSetData(equityCurveData);
                    if (success) {
                        if (!disposed) setLoading(false);
                        return;
                    }
                }
                
                // Priority 2: Fetch from dedicated equity curve API endpoint
                if (accountId) {
                    const data = await getEquityCurve(accountId, startDate, endDate);
                    if (disposed) return;
                    
                    if (data && data.length > 0) {
                        const success = processAndSetData(data);
                        if (success) {
                            if (!disposed) setLoading(false);
                            return;
                        }
                    }
                }
                
                // Priority 3: Fallback to client-side computation from trades prop
                if (trades && trades.length > 0) {
                    const fallbackData = buildEquityCurveFromTrades(trades);
                    if (fallbackData.length > 0) {
                        processAndSetData(fallbackData);
                        if (!disposed) setLoading(false);
                        return;
                    }
                }
                
                if (disposed) return;
                // No data available from any source
                setError('No equity curve data available. Complete some trades to see the equity curve.');
                
            } catch (err) {
                if (disposed) return;
                console.error('Error fetching equity curve:', err);
                
                // Try fallback from trades on API error
                if (trades && trades.length > 0) {
                    const fallbackData = buildEquityCurveFromTrades(trades);
                    if (fallbackData.length > 0) {
                        processAndSetData(fallbackData);
                        if (!disposed) setLoading(false);
                        return;
                    }
                }
                
                setError(err.message || 'Failed to fetch equity curve data.');
            } finally {
                if (!disposed) setLoading(false);
            }
        };

        fetchChartData();

        return () => {
            disposed = true;
            if (chartRef.current) {
                chartRef.current.remove();
                chartRef.current = null;
            }
            equitySeriesRef.current = null;
            upperSeriesRef.current = null;
            lowerSeriesRef.current = null;
        };
    }, [accountId, startDate, endDate, trades, equityCurveData]);

    return (
        <div className="p-4 rounded-lg bg-backgroundSurface border border-borderDefault h-full flex flex-col">
            {!hideTitle && <h2 className="text-textPrimary text-lg font-semibold mb-4">Equity Curve</h2>}
            {loading && <p className="text-textSecondary text-sm mb-2">Loading equity data...</p>}
            {error && <p className="text-yellow-500 dark:text-yellow-400 text-sm mb-2">{error}</p>}
            <div ref={chartContainerRef} className="flex-1 min-h-[300px]" style={{ width: '100%' }} />
            <div className="mt-2 flex items-center gap-4 text-xs text-textSecondary">
                <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-sky-500 inline-block"></span>
                    Equity
                </span>
                <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-emerald-500/40 inline-block" style={{ borderStyle: 'dashed', borderWidth: '1px', backgroundColor: 'transparent', borderColor: 'rgb(16 185 129 / 0.4)' }}></span>
                    Peak (Runup)
                </span>
                <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-red-500/40 inline-block" style={{ borderStyle: 'dashed', borderWidth: '1px', backgroundColor: 'transparent', borderColor: 'rgb(239 68 68 / 0.4)' }}></span>
                    Trough (Drawdown)
                </span>
            </div>
        </div>
    );
};

export default EquityCurveWidget;
